/**
 * Semantic colour tokens — the light/dark pairs every component resolves
 * through (`bg.surface`, `text.secondary`, `border.default`, …).
 *
 * Split out of `theme/index.ts` when that file crossed the repo's 1000-line
 * ceiling. This is the half that grows: `index.ts` now holds the scales and the
 * theme assembly, and this holds the mapping from role to palette step. The
 * accessibility reasoning for each choice lives in the comments below and is
 * enforced by `__tests__/brandContrast.test.ts`, which resolves these tokens
 * live rather than reading the palette file.
 */

import { DARK_DERIVED } from './colors.brand';
import { badgeTokens } from './semanticTokens.badge';
import { gradientTokens } from './semanticTokens.gradient';
import { interactiveTokens } from './semanticTokens.interactive';

/**
 * The one light-mode grey ink, shared by `text.secondary`, `text.muted` and
 * `text.subtle`.
 */
/** Dark-mode counterpart to GREY_INK. */
const DARK_GREY_INK = DARK_DERIVED.textMuted;

const GREY_INK = '#565B56';

export const semanticTokens = {
  // Viewport height tokens - use dynamic viewport on mobile to account for browser chrome
  sizes: {
    'viewport.height': {
      base: '100dvh', // Mobile: dynamic viewport excludes browser UI
      md: '100vh', // Desktop: standard viewport
    },
  },
  shadows: {
    'elevation.raised': {
      default: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      _dark:
        'rgba(0, 0, 0, 0.1) 0px 0px 0px 1px, rgba(0, 0, 0, 0.2) 0px 5px 10px, rgba(0, 0, 0, 0.4) 0px 15px 40px',
    },
  },
  colors: {
    // ── Code syntax highlighting ──────────────────────────────────────
    // From main's code-first-class work (#1380). These carry raw hex rather
    // than role references: a syntax theme is a tuned set of twelve hues that
    // have to stay distinguishable from EACH OTHER, which is a different
    // constraint from the brand roles and does not survive being mapped onto
    // them. Consumed via SYNTAX_CLASS_STYLES in lib/code-highlighting.
    'syntax.keyword': {
      default: '#7045D3',
      _dark: '#926FE4',
    },
    'syntax.string': {
      default: '#2F7C56',
      _dark: '#4DCB8C',
    },
    'syntax.number': {
      default: '#996224',
      _dark: '#DE9E54',
    },
    'syntax.comment': {
      default: '#6E6E6E',
      _dark: '#8C8C8C',
    },
    'syntax.function': {
      default: '#246DCD',
      _dark: '#3C84E2',
    },
    'syntax.variable': {
      default: '#2E7A70',
      _dark: '#51C8B8',
    },
    'syntax.type': {
      default: '#C9363A',
      _dark: '#DC575B',
    },
    'syntax.operator': {
      default: '#5A7182',
      _dark: '#7590A3',
    },
    'syntax.punctuation': {
      default: '#61707B',
      _dark: '#8B9BA7',
    },
    'syntax.builtin': {
      default: '#B13BB1',
      _dark: '#CF4BCF',
    },
    'syntax.literal': {
      default: '#2A7791',
      _dark: '#45B0D3',
    },
    'syntax.meta': {
      default: '#7A6E3F',
      _dark: '#BEAF74',
    },
    // Chakra ships its OWN semantic tokens (chakra-body-bg, chakra-subtle-text,
    // …) pointing at `gray.*` — a palette this theme no longer defines. Left
    // alone they resolve to nothing, so Chakra's default body and subtle
    // surfaces fall back to transparent. Repointed at roles here.
    'chakra-body-bg': { default: 'bg.canvas', _dark: 'bg.canvas' },
    'chakra-body-text': { default: 'text.primary', _dark: 'text.primary' },
    'chakra-inverse-text': { default: 'text.inverse', _dark: 'text.inverse' },
    'chakra-subtle-bg': { default: 'bg.subtle', _dark: 'bg.subtle' },
    'chakra-subtle-text': { default: 'text.secondary', _dark: 'text.secondary' },
    'chakra-border-color': { default: 'border.default', _dark: 'border.default' },
    'chakra-placeholder-color': { default: 'text.muted', _dark: 'text.muted' },
    // Background — the warm cream workspace canvas (spec --background).
    'bg.canvas': {
      default: 'surface.300', // #F8F7F4 — DESIGN.md canvas
      _dark: DARK_DERIVED.canvas,
    },
    // FLIPPED by the 2026-09 curved canvas. The page used to be parchment with
    // white cards raised off it; now the canvas panel is white (`frame.panel`)
    // and the boxes drawn ON it are parchment with a hairline. So this token
    // means "a content box on the canvas", and it is no longer white.
    //
    // What did NOT move: surfaces that float ABOVE the canvas on their own
    // elevation — modal, drawer, menu, popover — which stay white via
    // `bg.elevated`. The design sheet draws the search modal as a white sheet,
    // and a parchment menu over a parchment box has nothing to separate it.
    // `components.modal.ts` and the Menu theme point there deliberately; do not
    // "simplify" them back onto this token.
    'bg.surface': {
      default: 'surface.300', // #F8F7F4 — DESIGN.md content box
      _dark: DARK_DERIVED.surface,
    },
    // The resting / hover / active ladder must be THREE distinct steps. A
    // first cut gave `bg.subtle`, `bg.muted` and `bg.hover` all the same
    // `neutral.200`, which silently removed the hover feedback from every
    // component that rests on a subtle/muted tint (14 of them) — the handler
    // ran, the background never changed. Keep these three values apart.
    // `DARK_DERIVED.subtle`, not `.surface`. These were one value in dark until
    // 2026-09-22, which meant a quiet tint drawn on a box was the box, and —
    // because this token is ALSO the hover target for ten-odd controls — that
    // every one of them had no hover feedback whatever in dark. See the
    // `subtle` comment in colors.brand.ts for the list.
    'bg.subtle': {
      default: 'neutral.200', // #E5E3DE — one step down from the cream page
      _dark: DARK_DERIVED.subtle,
    },
    // `DARK_DERIVED.hover`, not `.elevated`, since the 2026-09 panel lift —
    // see that value's comment. In light this is unchanged.
    'bg.hover': {
      default: 'neutral.300',
      _dark: DARK_DERIVED.hover,
    },
    'bg.active': {
      default: 'neutral.350',
      _dark: DARK_DERIVED.active,
    },
    // Pairs with `bg.subtle` deliberately: both are `neutral.200` in light, so
    // splitting them in dark alone would invent a distinction the design does
    // not have. It moved off `.surface` for the same reason `bg.subtle` did.
    'bg.muted': {
      default: 'neutral.200',
      _dark: DARK_DERIVED.subtle,
    },
    'bg.elevated': {
      default: 'white',
      _dark: DARK_DERIVED.elevated,
    },
    // Modal scrim. Charcoal at 50%, not black at 60%: the 2026-09 design
    // dims the page rather than blacking it out, so the frame behind a search
    // sheet stays legible as context. Charcoal rather than pure black keeps
    // the warmth of the parchment underneath instead of greying it.
    //
    // 50%, NOT the 40% this first shipped at. The scrim is the only thing
    // separating the search sheet from the page: `CommandPalette` is white,
    // has no border by design, and floats over the white content panel — so
    // the sheet's edge IS the scrim boundary and 1.4.11's 3:1 applies to it.
    // Measured with `composite()` against the LIGHTEST backdrop the scrim can
    // fall on (the white panel, which is the worst case): 40% flattens to
    // #A6A9A6 — 2.37:1 against the sheet, under the bar — and 50% to #909390,
    // 3.11:1. Still far lighter than the 60% black it replaced (4.17:1), so
    // the frame stays legible as context. Pinned by
    // __tests__/surfacePairs.test.tsx, which measures rather than trusts this.
    //
    // Dark mode keeps the heavier black — a light scrim over an already-dark
    // canvas does not separate the sheet from the page at all.
    'bg.overlay': {
      default: 'rgba(33, 39, 33, 0.5)',
      _dark: 'blackAlpha.800',
    },
    // Selection is an accent moment — points at the accent ramp.
    'bg.selection': {
      default: 'accent.50',
      _dark: 'rgba(92, 136, 218, 0.18)',
    },

    // `tag.bg` / `tag.hover` / `tag.color` USED TO LIVE HERE. They were read by
    // exactly one module, `utils/tagColors.ts`, which this change deleted — so
    // they went with it, for the same reason `chip.*` did: a colour table
    // nothing renders is a table the next person will wire something up to by
    // mistake. Tags are Badges now; their colours are the `badge.*` rows below.

    ...badgeTokens,

    // Text colors
    // `ink.primary`, NOT `text.primary` — a semantic token whose default
    // names its own `--chakra-colors-*` var is circular and resolves to
    // empty. See the `ink` comment in colors.ts.
    'text.primary': {
      default: 'neutral.900', // #212721 — spec --foreground
      _dark: DARK_DERIVED.text,
    },
    // #6E6E6E, not the spec's #757575, so secondary text clears WCAG AA
    // (4.5:1) on the warm sidebar as well as the white canvas:
    //
    //            #FFFFFF   #F6F4F3 (sidebar.bg)
    //   #757575    4.61      4.20  ← fails AA on the sidebar
    //   #6E6E6E    5.10      4.65
    //
    // The left-hand chrome paints `text.secondary` as its resting nav ink
    // (the sidebar tree's rows), so the
    // failing pair is really on screen. colors.ts carried #6E6E6E as a
    // palette entry for exactly this reason, but the value never applied —
    // this semantic token silently overwrote it (the palette/semantic name
    // collision fixed in the previous commit), so the app shipped #757575
    // throughout.
    //
    // `_dark` is unchanged: #A3A3A3 already clears AA on every dark surface
    // (6.00:1 on the darkest, sidebar.item.active #262626).
    //
    // Pinned by __tests__/contrast.test.ts. NOT AA on the *hover* tints
    // (#ECEAE8 → 4.25) — text on tinted rows should use `text.onMutedBg`.
    'text.secondary': {
      default: GREY_INK, // #565B56 — spec --muted-foreground, 5.98:1 on cream
      _dark: DARK_GREY_INK,
    },
    // Same value as text.secondary, deliberately — not a copy-paste slip.
    // This token has been darkened twice for contrast: #A3A3A3 (2.52:1 on
    // white, fail) → #767676 (4.54:1, tuned against white only) → #6E6E6E.
    // #767676 was 4.14:1 on the warm sidebar, the worst of the greys, and
    // text.muted is the DOMINANT ink of the left-hand chrome (the nav rail and
    // workspace-drawer rows under components/layout/).
    //
    // There is no room left to keep it a lighter tier than text.secondary:
    // the lightest grey that clears 4.5:1 on #F6F4F3 is #707070 (4.52:1),
    // which is 2/255 from #6E6E6E — imperceptible. That gap is exactly why
    // #767676 and #757575 read as one colour today. Hierarchy below
    // text.secondary has to come from weight/size, not from a lighter grey,
    // unless the surface it sits on gets lighter.
    //
    // `_dark` unchanged: #8A8A8A clears AA on every resting dark surface
    // (4.78–5.59, floor on bg.elevated/bg.info #1F1F1F). It is 4.38:1
    // on the dark active tint #262626, but the left-hand chrome switches to
    // text.primary whenever a row goes hover/active, so that pair is never
    // painted there.
    // Text deliberately placed on a tint should use `text.onMutedBg`.
    'text.muted': {
      default: GREY_INK,
      _dark: DARK_GREY_INK,
    },
    // text.accent now resolves to primary ink — spec rule: don't apply purple
    // to default links / hover. Keeping the token for backwards compat.
    // Brand-coloured foreground. Resolves to the NAVY, not the twilight:
    // #5C88DA on the cream is 3.02:1, below the 4.5:1 AA floor for text.
    // See colors.brand.ts for the full role split.
    'text.accent': {
      default: 'accent.700', // #3F5C94 (twilight.700); see colors.brand.ts
      _dark: 'accent.400', // lifted; twilight clears AA on a dark canvas
    },
    // Link ink. ~29 call sites across the app pass `color="text.link"`, but
    // the token was never defined — Chakra then emitted the literal
    // `color: text.link` (invalid) and dropped it. For a Chakra <Link> the
    // component baseStyle (components.ts) still colored it ink, so those went
    // unnoticed; but non-<Link> callers (Spinner, MaterialIcon, plain Text)
    // silently inherited their parent's colour. Defining it here makes every
    // caller resolve to the same ink the app already uses for links (spec:
    // links are ink, not purple — mirrors text.accent).
    'text.link': {
      default: 'accent.700', // navy — see text.accent
      _dark: 'accent.400',
    },
    'text.inverse': {
      default: 'white',
      _dark: DARK_DERIVED.canvas,
    },
    // Always white. Pairs with brand.primary which is a rich purple in
    // BOTH modes — white text reads at 9:1 (light) and 6.5:1 (dark).
    // Previous attempt flipped this to dark in _dark and produced
    // inconsistent black-on-purple buttons across the app.
    'text.onBrand': {
      default: 'white',
      _dark: 'white',
    },
    // Text over a live camera image (the Live page's shutter controls). The
    // camera behind it is the backdrop in BOTH modes, so like text.onBrand it
    // never flips; the muted step is for the unselected PHOTO · VIDEO label.
    'text.onMedia': {
      default: 'white',
      _dark: 'white',
    },
    'text.onMediaMuted': {
      default: 'whiteAlpha.800',
      _dark: 'whiteAlpha.800',
    },
    // The hairline around an outline control over the camera (the Live step
    // card's Go to step and More). Fixed in both modes, like text.onMedia.
    'border.onMedia': {
      default: 'whiteAlpha.400',
      _dark: 'whiteAlpha.400',
    },
    // The pill behind that text, so it reads over a white bench too (white
    // on it is about 6.6:1, the muted step about 5:1, over pure white).
    'bg.mediaScrim': {
      default: 'blackAlpha.700',
      _dark: 'blackAlpha.700',
    },
    // The ink for a LIGHT filled control — today only the `warning` button.
    //
    // Fixed in both modes, exactly like `text.onBrand` above and for the same
    // reason: it is chosen to pair with a fill that does not change, so letting
    // it flip would break the pairing rather than preserve it. `text.primary`
    // is the trap here — near-black in light, near-WHITE in dark. On the
    // RESTING warning fill it reads 9.61:1 and then 1.37:1.
    //
    // Note the LIGHT figure cannot tell the trap from the fix: in light mode
    // `text.primary` and this token are the same hex. Dark is the entire
    // difference, which is why dark is what the test asserts. (An earlier
    // version of this note quoted "8.34 then 1.58" — both of those are the
    // HOVER step, and the pair was measured around a non-difference.)
    //
    // On `interactive.warning` this token measures 9.61 / 8.34 / 5.88 across
    // the three states, identically in both modes.
    'text.onWarning': {
      default: 'neutral.900',
      _dark: 'neutral.900',
    },

    // Borders — hairline at low contrast.
    'border.default': {
      default: 'neutral.300', // #DBDCDB — spec --border
      _dark: DARK_DERIVED.border,
    },
    // `DARK_DERIVED.hairline`, NOT `.elevated`. Pointing a hairline at a FILL
    // value held only while the page sat at #1C211C; the 2026-09 panel lift
    // moved the page to #202520 and took this border to 1.063:1 on it, which
    // is where the Protocol Library table lost its outline. See `hairline`.
    'border.muted': {
      default: 'neutral.200',
      _dark: DARK_DERIVED.hairline,
    },
    // border.accent + border.focus point at the ACCENT palette — focus
    // and selection are the moments where the accent communicates intent.
    // Per spec, the accent is reserved for AI/sparkle/focus moments.
    'border.accent': {
      default: 'accent.500',
      _dark: 'accent.300',
    },
    'border.hover': {
      default: 'neutral.400',
      _dark: DARK_DERIVED.border,
    },
    // Form-field chrome. Deliberately a touch stronger than `border.default`
    // — a field needs to read as an editable box, not a hairline divider (see
    // the Input baseStyle comment in components.ts).
    //
    // These exist so components.ts can express the light/dark pair as ONE
    // declaration. A `_dark: { borderColor }` block sitting beside `_focus` /
    // `_invalid` in a style object emits a (0,3,0) selector that outscores
    // their (0,2,0), so the focus and invalid borders never painted in dark
    // mode. A semantic token emits a single (0,1,0) declaration and switches
    // via the CSS var, so it cannot outscore anything.
    // Pinned by __tests__/componentDarkModeSpecificity.test.ts.
    // DELIBERATE DEVIATION from spec --border. A form field's outline is
    // often the ONLY thing marking where the control is, which WCAG 2.2
    // 1.4.11 holds to 3:1 — and #DBDCDB on the cream page is 1.19:1. The
    // hairline value stays on `border.default` for decorative rules; input
    // boundaries step down the sand ramp until they clear the threshold.
    //
    // `neutral.500` (#7E817E) is where that actually happens: 3.94:1 on white,
    // 3.40 on the cream canvas, 3.07 on `bg.subtle`. neutral.400 was the first
    // choice and reaches only 1.95 / 1.68 — an improvement on the hairline,
    // but nowhere near the bar this comment claims. Pinned by
    // brandContrast.test.ts so the next tweak can't quietly re-cross it.
    // Confirm against page 10 of the guidelines when it lands.
    'border.field': {
      default: 'neutral.500',
      _dark: 'whiteAlpha.300',
    },
    'border.field.hover': {
      default: 'neutral.600',
      _dark: 'whiteAlpha.400',
    },
    // Focus ring — accent.600, the darkest-but-still-brand step, because
    // 1.4.11's 3:1 has to hold against the DARKEST surface the ring is drawn
    // on (an active sidebar row, #D5D2CA), not the lightest. See the
    // `shadows.outline` comment for the measurements.
    'border.focus': {
      default: 'accent.600',
      _dark: 'accent.400',
    },
    // Shares `hairline` with `border.muted` — the two are `neutral.200` in
    // light as well, so they stay one material in both modes.
    'border.subtle': {
      default: 'neutral.200',
      _dark: DARK_DERIVED.hairline,
    },
    'border.warning': {
      default: 'warning.200',
      _dark: 'warning.700',
    },

    // Brand — twilight blue primary. Bower's signature CTA color.
    // EXPERIMENT: brand identity colors are locked to the same shade in
    // both light and dark mode so the brand reads consistently regardless
    // of theme. This is how Stripe (blurple) and Linear (indigo) handle
    // their primary CTAs. Tint surfaces (brand.muted, brand.surface) are
    // also locked — note these may read as near-white cards on a dark
    // canvas, since a light brand tint loses its "subtle wash" function
    // when the canvas is dark.
    // Solid CTA fill. NAVY, not the twilight: these buttons carry a white
    // label, and white on #5C88DA is 3.51:1 — below the 4.5:1 AA floor.
    // Navy gives 9.70:1. The hover step moves TOWARD the twilight rather
    // than away, so the ramp still reads as the brand blue on interaction.
    // DESIGN.md draws the solid CTA as Twilight 500 with a white label. That
    // is 3.51:1 — fine for the LARGE button (20px semibold counts as large
    // text, 3:1) and a fail for the default (16px, 4.5:1). Same fill, two
    // sizes, one compliant. So the whole ladder shifts down one step:
    // rest 600 (4.77:1), hover 700 (6.62:1), active 800 (9.70:1). That keeps
    // the spec's DIRECTION — darkens on interaction — and stays as close to
    // the brand blue as AA allows. Twilight 500 remains the identity colour
    // for fills, focus rings and display text >=24px (see accent.solid).
    'brand.primary': {
      default: 'accent.600', // #4D72B7
      _dark: 'accent.600',
    },
    // Brand purple used as a FOREGROUND (text / icons) sitting on a light or
    // dark surface. Unlike brand.primary (locked to brand.500 for the solid-
    // button BACKGROUND role, where white-on-brand needs the darker shade),
    // brand text must clear WCAG AA 4.5:1. brand.500 passes on white
    // (4.54:1) but only reaches 3.99:1 on the dark bg.surface (#161616), so
    // this token lifts one step to brand.400 in dark (4.64:1) — still
    // unmistakably the brand purple. Use for any brand-coloured text/icon;
    // keep brand.primary for backgrounds, borders, and focus rings.
    // Brand colour used as a FOREGROUND on a surface. Same split as
    // text.accent: navy in light (twilight is 3.02:1 on the cream), and a
    // lifted twilight in dark. `accent.300` not `400` — the derived dark
    // surfaces are lighter than the ones this token was tuned against, and
    // 400 lands at 4.19:1. Pinned by contrast.test.ts.
    'brand.onSurface': {
      default: 'accent.700',
      _dark: 'accent.300',
    },
    'brand.secondary': {
      default: 'neutral.200',
      _dark: DARK_DERIVED.surface,
    },
    'brand.secondary.hover': {
      default: 'neutral.300',
      _dark: DARK_DERIVED.chromeActive,
    },
    'brand.secondary.active': {
      default: 'neutral.400',
      _dark: DARK_DERIVED.border,
    },
    'brand.highlight': {
      default: 'decorativeWarm.600',
      _dark: 'decorativeWarm.300',
    },
    'brand.alert': {
      default: 'decorativeDeep.400',
      _dark: 'decorativeDeep.300',
    },
    'brand.emphasis': {
      default: 'accent.700', // #2E446D — deep emphasis (the navy)
      _dark: 'accent.700',
    },
    // Tinted SURFACES (chat bubble bg, info-style cards, brand-tinted
    // chips). Light mode = pale purple wash on white canvas. Dark mode =
    // translucent purple over the dark canvas — using rgba so the wash
    // composites correctly regardless of what sits beneath.
    'brand.muted': {
      default: 'accent.50',
      _dark: 'rgba(92, 136, 218, 0.18)',
    },
    // The hairline that pairs with `brand.muted`. A brand-tinted fill alone
    // dissolves into the canvas at low opacity — especially in dark, where
    // an 18% wash over navy is barely a shape. The outline is what makes it
    // read as a deliberate object rather than a smudge, and it lets the fill
    // stay quiet enough to survive a long pasted message. Light is
    // brand.500 at 16%, dark lifts to 38% to hold its edge on the dark
    // canvas. Always use WITH brand.muted, never as a standalone border.
    'brand.mutedBorder': {
      default: 'rgba(92, 136, 218, 0.16)',
      _dark: 'rgba(92, 136, 218, 0.38)',
    },
    // Ink for small text sitting ON a tint. The plain foreground tokens are
    // scaled for bg.surface and fall under AA once the background is tinted:
    // text.success on bg.success is 4.16:1 and text.secondary on bg.muted is
    // 4.19:1 in light mode. These pairs are verified ≥4.5:1 on their own
    // tint in BOTH modes — use them for pills, badges and chips, never the
    // bare text.* token.
    'text.onSuccessBg': {
      default: 'success.600', // 5.50:1 on green.50
      _dark: 'success.300', // 6.44:1 on green.900
    },
    // `neutral.700`, a step DARKER than GREY_INK. It was #5F5F5F, which is
    // lighter than today's #565B56 plain ink — an on-tint ink that is weaker
    // than the ink it exists to replace, and 4.23:1 on an active sidebar row.
    'text.onMutedBg': {
      default: 'neutral.700', // #3F443F — 6.59:1 on the darkest resting tint
      // Same inversion as the light half had, mirrored: #A3A3A3 is DARKER
      // than the plain dark grey ink (#B4B9B4), so the "stronger on a tint"
      // token was the weaker of the two. On a dark surface stronger means
      // lighter.
      _dark: '#C9CDC9',
    },
    'brand.surface': {
      default: 'accent.50',
      _dark: 'rgba(92, 136, 218, 0.12)',
    },
    // The hairline between the two halves of a SPLIT action button — the
    // primary action and the chevron that opens its alternatives.
    //
    // It exists because `ButtonGroup isAttached` collapses the two buttons into
    // one shape, which on a filled button means the chevron half has no visible
    // boundary at all and the whole control reads as one very wide button.
    'button.split.divider': {
      // accent.200, not the sheet's Twilight 400. Measured on the fill this is
      // actually drawn on: 400 is 1.92:1 on `brand.primary` (accent.600) and
      // 2.67:1 on the accent.700 hover — a seam that is not there. 200 gives
      // 3.37:1 and 4.68:1, clearing 1.4.11's 3:1 in both states while staying
      // a seam rather than a border. No `_dark`: the fill is the same brand
      // step in both modes, so a second value would only invite drift.
      default: 'accent.200',
    },
    'brand.hover': {
      default: 'accent.700', // #3F5C94 — 6.62:1 under a white label
      _dark: 'accent.700',
    },
    'brand.active': {
      default: 'accent.800', // deepest on press
      _dark: 'accent.800',
    },

    // Accent — the deliberate-moment palette. Resolved from the `accent.*`
    // ramp in colors.ts (currently same shades as brand, can diverge).
    // Reserved for AI / sparkles / avatar fallback / focus rings /
    // selections / premium badges. NEVER use on default buttons, links, or
    // generic borders — those use `brand.primary` and `border.default`.
    // The twilight lives here: accent moments are icons, fills and large
    // display text, all judged at the 3:1 non-text / large-text threshold.
    'accent.solid': {
      default: 'accent.500', // #5C88DA — spec --primary
      _dark: 'accent.400',
    },
    'accent.bg': {
      default: 'accent.50',
      _dark: 'rgba(92, 136, 218, 0.15)',
    },
    'accent.fg': {
      default: 'white',
      _dark: 'white',
    },
    'accent.border': {
      default: 'accent.200',
      _dark: 'accent.700',
    },

    // Error / Warning
    'bg.error': {
      default: 'danger.50',
      _dark: 'danger.900',
    },
    // The PRESSED step for `outline-danger` / `ghost-danger`, so their
    // resting / hover / active ladder is three values and not two (the rule at
    // the top of this file, and the incident behind it: three tokens that
    // collapsed onto one `neutral.200` removed the hover feedback from 14
    // components while every handler still ran).
    //
    // IN DARK IT IS DELIBERATELY EQUAL TO `bg.error`, and that is a palette
    // limit rather than an oversight. `bg.error` is already the ramp's darkest
    // step there (`danger.900`), so a distinct press step has to go LIGHTER,
    // and the next one up puts `text.error` at 3.90:1 — under 1.4.3. Light mode
    // gets its three steps (7.21 / 6.54 with the same ink); dark keeps two and
    // the equality is asserted, so widening the ramp shows up as a failing test
    // rather than as a press nobody can see.
    'bg.error.active': {
      default: 'danger.100',
      _dark: 'danger.900',
    },
    'border.error': {
      default: 'danger.200',
      _dark: 'danger.700',
    },
    // Semantic roles, per DESIGN.md "Semantic tokens": the tokens here ship
    // THREE deliberate deviations, each because the shade as drawn fails WCAG
    // AA. Measured on the canvas (Parchment 300) and under a white label:
    //
    //   role          spec           as text    white label   shipped
    //   Information   Bluebird 700   4.32 FAIL  4.63 ok       Bluebird 800 (6.84 / 7.33)
    //   Warning       Sunset   700   3.60 FAIL  3.85 FAIL     Sunset   800 (5.89 / 6.31)
    //   Action        Twilight 500   3.27 FAIL  3.51 FAIL     fill = twilight.600
    //
    // Information and Warning are `text.info` and `text.warning` below. Action
    // keeps Twilight 500 as the IDENTITY colour (fills, focus rings, display
    // text >=24px) but a solid button carrying a white 16px label resolves to
    // 600 (`brand.primary` above), which is where DESIGN.md already puts the
    // hover state. The ladder therefore shifts down a step rather than losing
    // the brand blue: rest 600, hover 700.
    //
    // Success (Emerald 700) and Error (Poppy 700) pass as drawn and are
    // unchanged. Pinned by brandContrast.test.ts, which fails if any of these
    // regress.
    //
    // Status inks are measured against the CREAM canvas, not white. Moving
    // the canvas cost every one of them ~14% of its ratio, which pushed
    // success and warning under AA where they had passed on white
    // (4.76→4.11 and 4.92→4.25). They are one step darker here as a result.
    // Dark mode moved the other way — the derived surfaces are LIGHTER than
    // the near-blacks these were tuned against — so the dark halves lift.
    'text.error': {
      default: 'danger.700', // DESIGN.md Error — 7.20:1 on canvas
      _dark: 'danger.400', // red.500 was 4.35:1 on the warmed bg.surface
    },
    'text.warning': {
      default: 'warning.800', // DESIGN.md Warning, one step darker: 700 is 3.60:1 (FAILS)
      _dark: 'warning.400',
    },
    'bg.warning': {
      default: 'warning.50',
      _dark: 'warning.900',
    },
    // The PRESSED step for `outline-warning`, so its resting / hover / active
    // ladder is three values and not two (the ladder rule above).
    //
    // IN DARK IT IS DELIBERATELY EQUAL TO `bg.warning`, the same palette limit
    // `bg.error.active` carries: `bg.warning` is already the ramp's darkest
    // step there, and the next one up puts `text.warning` at 3.98:1, under
    // 1.4.3. Light gets its three steps (6.09 / 5.74); the equality is
    // asserted so widening the ramp surfaces it.
    'bg.warning.active': {
      default: 'warning.100',
      _dark: 'warning.900',
    },

    // Status
    'status.complete': {
      default: 'success.600', // green.500 (spec --success) is 4.11:1 on cream
      _dark: 'success.400',
    },
    'status.flagged': {
      default: 'danger.500',
      _dark: 'danger.400',
    },
    'status.inProgress': {
      default: 'info.500',
      _dark: 'info.400',
    },
    'status.toBeTestedBackground': {
      default: 'warning.100',
      _dark: 'warning.800',
    },
    'status.toBeTested': {
      default: 'warning.800', // see text.warning
      _dark: 'warning.200',
    },
    'status.success': {
      default: 'success.600', // see status.complete
      _dark: 'success.400',
    },
    'status.pending': {
      default: 'warning.500',
      _dark: 'warning.400',
    },

    'bg.success': {
      default: 'success.50',
      _dark: 'success.900',
    },
    // The PRESSED step for `outline-success`, so its resting / hover / active
    // ladder is three values and not two (the ladder rule above).
    //
    // Both modes get three real steps here (6.88 / 6.32 light, 8.90 / 6.28
    // dark), unlike `bg.error.active` and `bg.warning.active`, whose dark
    // press step would drop the ink under AA.
    'bg.success.active': {
      default: 'success.100',
      _dark: 'success.800',
    },
    'text.success': {
      default: 'success.700', // DESIGN.md Success — 6.78:1 on canvas
      _dark: 'success.300', // green.400 was 4.52:1 on the warmed bg.elevated
    },
    'border.success': {
      default: 'success.200',
      _dark: 'success.700',
    },

    // Info — quiet neutral now (purple is reserved for sparkle moments).
    'bg.info': {
      default: 'info.50',
      // `info.900`, matching `bg.error`, `bg.warning` and `bg.success`, which
      // all pair `<ramp>.50` with `<ramp>.900`. This alone was
      // `DARK_DERIVED.surface` - #1C211C, the CARD colour - so in dark mode
      // the info tone was whatever it sat on, and the four surfaces that ask
      // for it (the protocol value chip, `StatusBanner tone='info'`, the
      // connector import panel, `UnrecoveredNotice`) all read as untinted.
      //
      // The chip is where it bit hardest: its own comment records moving OFF
      // `bg.subtle` because "#1C211C sat at contrast 1.00 against the card it
      // was on and was invisible as a chip", and the token it moved TO
      // resolved to that same #1C211C. Light mode was correct throughout,
      // which is how it shipped.
      _dark: 'info.900',
    },
    'text.info': {
      default: 'info.800', // DESIGN.md Information, one step darker: 700 is 4.32:1 (FAILS)
      // `info.300`, the step `text.success` uses, measuring 8.08:1 on the
      // `info.900` fill. This was `DARK_DERIVED.text` - the BODY INK - so in
      // dark mode the one token named for the tone carried none of it, and a
      // value chip's own comment claiming "info blue, not primary ink" was
      // false. Contrast drops from 10.34:1, which is the point rather than a
      // cost: both clear AA comfortably, and only one of them says `info`.
      _dark: 'info.300',
    },
    'border.info': {
      default: 'info.200',
      // `info.700`, matching `border.success` / `border.error` /
      // `border.warning`, at 3.54:1 against the card a banner sits on.
      //
      // It was `DARK_DERIVED.elevated`: 1.12:1 against that card, which is to
      // say there was no visible edge. The adjacent colour that matters for a
      // banner's border is the CARD, not the fill it encloses (2.59:1), since
      // the edge is what separates the banner from the page.
      _dark: 'info.700',
    },

    'text.subtle': {
      // Aliased to text.muted — the shared GREY_INK constant is what keeps the
      // alias honest; it used to be a hand-copied literal. Was #A3A3A3 (2.52:1
      // on white, fail), then #767676 (4.14:1 on the warm sidebar, fail). See
      // the text.muted comment for the full history.
      default: GREY_INK,
      _dark: DARK_GREY_INK,
    },

    ...interactiveTokens,

    // Tables
    // `hairline`, with `border.muted` / `border.subtle` — same material, same
    // job. It kept `elevated` when they moved and measured 1.115:1 on
    // `bg.surface`, under this theme's own hairline floor. No live consumer
    // (the TipTap table draws its own `--tt-table-border-color`), so this is
    // the cheap half of the CR-025 sibling sweep rather than a visible fix.
    'table.border': {
      default: 'neutral.300',
      _dark: DARK_DERIVED.hairline,
    },
    'table.headerBg': {
      default: 'neutral.200',
      _dark: DARK_DERIVED.chrome,
    },

    // Code
    // `surface.400` (#F4F2EE), not `neutral.200` (#E5E3DE). The twelve syntax
    // hues below were tuned by #1380 against a #F5F4F3 ground; on the darker
    // neutral they drop to 3.96:1, under the 4.5:1 floor. This is the merge
    // hazard git cannot flag — main's delta and this branch's delta each pass
    // CI alone and only interact once combined. #F4F2EE is the step in the new
    // Parchment ramp closest to what those hues were drawn for.
    'code.bg': {
      default: 'surface.400',
      _dark: DARK_DERIVED.chrome,
    },
    'code.text': {
      default: 'text.primary',
      _dark: DARK_DERIVED.text,
    },

    // Sidebar — the SAME white the page canvas is drawn on, deliberately.
    //
    // It was `ui.sidebar` (#E9E7E1), a warm grey, back when the chrome sat
    // flush against a parchment page and needed a tone to separate it. The
    // 2026-09 curved canvas removed that job: the panel is now an inset card
    // with a 12px gutter and a shadow, so the gap and the elevation do the
    // separating and a second background tone only made the chat and the
    // drawer read as a different material from the page beside them.
    //
    // Kept as its own token rather than collapsed into `frame.panel`: its
    // consumers (LeftPanelColumn and the panels in it) are all one surface, and "the left panel's fill" is still a role that could
    // earn its own value again. Same reasoning as the panel-width constants
    // in `nav/navRailWidth.ts`.
    //
    // The item hover/active steps below MOVED WITH IT — see their comment.
    'sidebar.bg': {
      default: 'white',
      _dark: DARK_DERIVED.panel,
    },
    // `hairline`, NOT `elevated`, and this is the LIVE one of the three
    // siblings swept with `border.muted`/`border.subtle` on 2026-09-22.
    // Against its own ground (`sidebar.bg` = `DARK_DERIVED.panel`) it measured
    // 1.063:1 — byte-for-byte the number the `panel` docblock names as the
    // defect this whole change exists to remove — and it is drawn on three
    // real surfaces: the left panel's edge, `NavRailFooter` and the (since
    // retired) workspace drawer's chip. Fixing the hairline class everywhere EXCEPT the
    // sidebar would have been the subset remedy CR-025 is about.
    'sidebar.border': {
      default: 'ui.sidebarBorder',
      _dark: DARK_DERIVED.hairline,
    },
    // The resting / hover / active ladder must stay THREE distinct steps —
    // the same rule the `bg.subtle`/`bg.muted`/`bg.hover` comment above states,
    // and the same trap. In DARK these used to be `chromeHover` (#1C211C) and
    // `chromeActive` (#252B25) against a `chrome` (#111511) panel. Moving the
    // panel to `DARK_DERIVED.surface` makes it #1C211C — exactly `chromeHover`
    // — so the hover would have become invisible in dark mode while every
    // handler still ran and every test still passed. They step up with it.
    //
    // Light needed no move: the steps were already below `ui.sidebar`, so
    // against white they read as a larger jump, not a lost one.
    //
    // They stepped up a SECOND time when the panel lifted to
    // `DARK_DERIVED.panel` (#202520) to separate it from the boxes drawn on
    // it. Left where they were, hover would have been #242A24 against #202520
    // — four values apart, half the separation it had — which is the same
    // silent-loss failure as the paragraph above, just slower.
    //
    // They take the SAME `hover` / `active` steps as `bg.hover` / `bg.active`
    // above, deliberately. A first cut of this lift gave the nav its own pair
    // and left the generic ones behind, which put a nav row and the collapse
    // button beside it — same ground, same gesture — on visibly different
    // hover strengths (1.201 vs 1.063). One ladder, every control on the page.
    'sidebar.item.hover': {
      default: 'ui.sidebarAccent',
      _dark: DARK_DERIVED.hover,
    },
    'sidebar.item.active': {
      default: 'ui.sidebarActive',
      _dark: DARK_DERIVED.active,
    },
    // Inverted chip — the workspace drawer's SELECTED filter pill. The drawer
    // is otherwise all warm greys, so an ink fill is the strongest available
    // "this filter is on" signal without spending the accent, which the spec
    // reserves for AI / sparkle / focus moments.
    //
    // A token pair rather than `bg="text.primary"` at the call site for two
    // reasons: the React rules ban palette refs like `gray.900` in components,
    // and `text.primary` does not work as a background — it is one of six
    // semantic tokens whose `default` names its own palette path, so Chakra
    // emits a circular `var(--chakra-colors-text-primary)` that resolves to
    // nothing. Backgrounds then fall back to transparent and borders to
    // `currentColor`, which is exactly how the first cut of this chip rendered
    // as white-on-white.
    'chip.selected.bg': {
      default: 'neutral.900', // #212721 — the brand ink, not the cool gray.900
      _dark: DARK_DERIVED.text,
    },
    'chip.selected.fg': {
      default: '#FFFFFF',
      _dark: DARK_DERIVED.canvas,
    },

    // Menu / sidebar interactive
    'menu.item.hover': {
      default: 'neutral.300',
      _dark: DARK_DERIVED.chromeHover,
    },
    'menu.item.focus': {
      default: 'neutral.300',
      _dark: DARK_DERIVED.chromeHover,
    },
    'menu.item.active': {
      default: 'neutral.400',
      _dark: DARK_DERIVED.chromeActive,
    },

    // Entities — copper / brown / sage retained for legacy differentiation.
    // Stage 2 will revisit whether entity icons stay warm or move neutral.
    'entity.workspace.color': {
      default: 'decorativeWarm.500',
      _dark: 'decorativeWarm.100',
    },
    'entity.workspace.bg': {
      default: 'decorativeWarm.50',
      _dark: 'rgba(74, 61, 47, 0.35)',
    },
    // Entity.project — uses the legacy copper ramp (was named "accent"
    // before the brand-accent palette was renamed). Stage 2 may move this
    // to a different palette when the entity icon system is reviewed.
    'entity.project.color': {
      default: 'decorativeWarm.600',
      _dark: 'decorativeWarm.300',
    },
    'entity.project.bg': {
      default: 'decorativeWarm.50',
      _dark: 'rgba(137, 89, 57, 0.15)',
    },
    'entity.note.color': {
      default: 'decorativeWarm.600',
      _dark: 'decorativeWarm.300',
    },
    'entity.note.bg': {
      default: 'decorativeWarm.50',
      _dark: 'rgba(94, 125, 79, 0.15)',
    },
    'entity.attachment.color': {
      default: 'decorativeWarm.600',
      _dark: 'decorativeWarm.300',
    },
    'entity.attachment.bg': {
      default: 'decorativeWarm.50',
      _dark: 'rgba(94, 125, 79, 0.15)',
    },

    // A workspace logo's inner edge (`components/workspace/WorkspaceLogo`):
    // needed on the light frame, a halo on the dark one.
    'logo.tile.ring': { default: 'blackAlpha.100', _dark: 'transparent' },

    // Card
    'card.bg': {
      default: 'ui.card',
      _dark: DARK_DERIVED.surface,
    },
    // `hairline`, with the other two. Reached only by the `Card` `project`
    // variant, which no call site uses, so like `table.border` this is swept
    // for consistency rather than for a visible defect.
    'card.border': {
      default: 'ui.border',
      _dark: DARK_DERIVED.hairline,
    },
    'card.shadow': {
      default: 'rgba(0, 0, 0, 0.06)',
      _dark: 'rgba(0, 0, 0, 0.5)',
    },

    // Graph visualizer canvas. Flips with the app theme (off-white in
    // light, near-black in dark). The renderer wraps nodes in an emissive
    // material so colours stay vivid against a light background and glow
    // against a dark one — see GraphCanvas3D.tsx.
    //
    // This is the ONLY graph colour that lives here. Node and edge colours
    // are raw hex in components/graph/nodeStyle.ts, which is the documented
    // exception to the semantic-token rule: both renderers feed them
    // straight into THREE.Color and Canvas2D, neither of which resolves
    // `var(--chakra-*)`. A parallel set of `graph.node.*` / `graph.edge.*`
    // tokens used to sit here in a different palette entirely, consumed by
    // nothing; it was removed rather than reconciled.
    'graph.bg': {
      default: '#FAFAFA', // warm off-white
      _dark: '#0A0A12', // deep blue-black, less harsh than pure black
    },

    // ── App frame (2026-09 curved canvas) ─────────────────────────────
    // The authenticated shell is a PARCHMENT FRAME with a white content panel
    // inset inside it — the design pass flipped the old scheme, where the page
    // was parchment and cards were white.
    //
    // This is a NEW token rather than a repoint of `bg.canvas`, deliberately.
    // `bg.canvas` has 62 consumers including the auth pages, the onboarding
    // wizard, every mobile capture sheet, the TipTap editor surface and
    // PrintLightMode — none of which are inside this frame and none of which
    // the design covers. Repointing it would move all of them silently, which
    // is the blast-radius mistake this rule exists to prevent.
    //
    // Scope: the shell frame (`layout/Layout.tsx`) and anything painting the
    // WHITE PANEL FILL — the Button `topbar` variant, `DetailTopBar`,
    // `SplitViewContainer`'s right pane, `BirdChatPanel`, the ToC panel and the
    // Capture Stream sticky bar. Content that wants a raised CARD on the panel
    // still uses `bg.surface` / `bg.elevated`, which is why `RowList`,
    // `TableShell` and the themed `Kbd` are parchment and must stay that way. The nav
    // rail is NOT a consumer: it is transparent.
    //
    // An earlier version of this block claimed the scope was Layout plus the
    // rail, with everything inside the panel unchanged. That was wrong in both
    // directions and it cost three bugs, all the same shape: `bg.surface`
    // flipped white -> parchment under this token, so every FULL-BLEED surface
    // still painting `bg.canvas` or `bg.surface` became either a grey band on
    // the white panel (`DetailTopBar`, the Capture Stream bar) or an invisible
    // divide (`SplitViewContainer`'s two panes, which went byte-identical).
    // The blast radius the paragraph above reasons about so carefully is real
    // — it just landed on the surfaces the scope sentence said were safe.
    'frame.bg': {
      default: 'surface.500', // #F0EEE9 — DESIGN.md page frame
      _dark: DARK_DERIVED.canvas,
    },
    // The panel the app content is drawn on: white in light, the raised dark
    // panel tone in dark so it still reads as lifted off the frame.
    //
    // `DARK_DERIVED.panel`, NOT `.surface`: sharing one value with `bg.surface`
    // / `bg.subtle` / `bg.muted` made every box drawn on the panel byte-
    // identical to it in dark. See the comment on `panel` in colors.brand.ts.
    'frame.panel': {
      default: 'white',
      _dark: DARK_DERIVED.panel,
    },

    ...gradientTokens,
  },
};
